
import React, { useState } from 'react';
import { User, CalendarEvent } from '../types';
import { MOCK_EVENTS } from '../mockData';
import { Clock, MapPin, Plus, X, Calendar as CalIcon, Info } from 'lucide-react';

interface CalendarProps {
  user: User;
}

const Calendar: React.FC<CalendarProps> = ({ user }) => {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const getMonthName = (dateStr: string) => {
    return 'Мај'; // Simplified for demo
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Календар на заедницата</h1>
          <p className="text-slate-500">Настани, задачи за одржување и состаноци.</p>
        </div>
        <button className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-indigo-700 transition-all flex items-center">
          <Plus className="w-5 h-5 mr-2" /> Додај настан
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {MOCK_EVENTS.map(event => (
            <div 
              key={event.id} 
              onClick={() => setSelectedEvent(event)}
              className="bg-white rounded-2xl border border-slate-200 p-6 flex space-x-6 hover:shadow-lg hover:border-indigo-200 transition-all cursor-pointer group"
            >
              <div className="flex-shrink-0 w-16 h-16 bg-indigo-50 rounded-2xl flex flex-col items-center justify-center border border-indigo-100 group-hover:bg-indigo-600 transition-colors">
                <span className="text-xs font-bold text-indigo-400 group-hover:text-indigo-200 uppercase">{getMonthName(event.date)}</span>
                <span className="text-2xl font-black text-indigo-700 group-hover:text-white">{event.date.split('-')[2]}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600">
                    {event.type}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">{event.title}</h3>
                <p className="text-slate-500 text-sm mb-4 line-clamp-1">{event.description}</p>
                <div className="flex items-center space-x-4 text-xs font-medium text-slate-400">
                  <div className="flex items-center">
                    <Clock className="w-3.5 h-3.5 mr-1" /> {event.time || '10:00 - 12:00'}
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-3.5 h-3.5 mr-1" /> {event.location || 'Заедничка просторија'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-4 uppercase text-xs tracking-widest">Филтрирај календар</h3>
            <div className="space-y-2">
              {['Социјални настани', 'Состаноци на Куќен Совет', 'Одржување', 'Изнасување ѓубре'].map(filter => (
                <label key={filter} className="flex items-center space-x-3 cursor-pointer group">
                  <input type="checkbox" className="w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500" defaultChecked />
                  <span className="text-sm text-slate-600 group-hover:text-slate-900 transition-colors">{filter}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="bg-indigo-600 rounded-2xl p-6 text-white shadow-xl shadow-indigo-100 relative overflow-hidden group">
            <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:scale-110 transition-transform">
              <CalIcon className="w-32 h-32" />
            </div>
            <h3 className="font-bold mb-2 text-lg">Сакате да резервирате простор?</h3>
            <p className="text-indigo-100 text-xs mb-6 relative z-10">Заедничката сала и кровната градина се достапни за приватни настани и родендени.</p>
            <button className="w-full py-3 bg-white text-indigo-600 rounded-xl text-sm font-bold hover:bg-indigo-50 transition-colors shadow-lg shadow-black/10">
              Резервирај сега
            </button>
          </div>
        </div>
      </div>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setSelectedEvent(null)} />
          <div className="relative bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-indigo-600 p-8 text-white">
              <div className="flex justify-between items-start mb-4">
                <span className="px-3 py-1 rounded-full bg-indigo-500/50 text-xs font-bold uppercase tracking-widest border border-indigo-400">
                  {selectedEvent.type}
                </span>
                <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-indigo-500 rounded-full transition-colors text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <h2 className="text-3xl font-black mb-2">{selectedEvent.title}</h2>
              <div className="flex items-center space-x-4 text-indigo-100 text-sm font-semibold">
                <div className="flex items-center">
                  <CalIcon className="w-4 h-4 mr-1.5" /> {selectedEvent.date}
                </div>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1.5" /> {selectedEvent.time || '10:00 - 12:00'}
                </div>
              </div>
            </div>
            
            <div className="p-8">
              <div className="flex items-center mb-6 text-slate-700 font-bold bg-slate-50 p-4 rounded-2xl border border-slate-100">
                <MapPin className="w-5 h-5 mr-3 text-indigo-500" />
                {selectedEvent.location || 'Заедничка просторија'}
              </div>
              
              <div className="mb-8">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center">
                  <Info className="w-3 h-3 mr-1.5" /> Опис на настанот
                </h4>
                <p className="text-slate-600 leading-relaxed font-medium">
                  {selectedEvent.description}
                </p>
              </div>
              
              <button 
                onClick={() => setSelectedEvent(null)}
                className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg shadow-slate-100"
              >
                Разбрав
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
